// Third-party
import React, { CSSProperties } from 'react';

// Components
import ComparisonCardContent from './ComparisonCardContent';
import { LoadingSpinner } from '@/views/components/common';

// Types
import Alt from '@/types/models/alt';
import AppRegion from '@/types/app-region';
import Benchmark from '@/types/benchmark';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemGroup from '@/types/comparison-item-group';
import Dictionary from '@/types/dictionary';
import Fund from '@/types/fund';
import Holding from '@/types/holding';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import RiskPeriods from '@/types/risk-periods';
import SelectedCategories from '@/types/selected-categories';
import Stock from '@/types/stock';
import User from '@/types/user';

// Utils
import dom from '@/utils/dom';
import { holdingsToComparisonItems } from '@/utils/comparison';

type State = {
  holdingItems: ComparisonItem[];
  loadingItems: boolean;
};

type Props = {
  item: ComparisonItem;
  sharingInfo?: Nullable<{ isMine: boolean; label: string }>;
  readOnly?: boolean;
  user?: User | null;
  comparison: Comparison;
  editedPortfolio?: Portfolio;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  addCategoryBenchmark: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  categories: ComparisonCategory[];
  markupMode?: boolean;
  markupTool?: string;
  onCellClick: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => void;
  viewMarkup: boolean;
  selectedCategories?: SelectedCategories;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  getNewDotNumber: () => number;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  updateTableHeight: (height: number) => void;
  resizeCommentLinesContainer: () => void;
  riskPeriod: RiskPeriods;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  getItemHoldingsData: (holdings: ComparisonHolding[]) => Promise<{ alts: Alt[]; funds: Fund[]; stocks: Stock[]; benchmarks: Benchmark[] }>;
  onExpandPortfolioItem: (item: ComparisonItem, expand: boolean) => void;
  previewMarkup?: boolean;
  getAdditionalCategoryStyles: (category: ComparisonCategory) => CSSProperties;
  disableStickyHeader?: boolean;
  onModifyBenchmark: (item: ComparisonItem) => void;
  updateComparisonItemHolding: (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
  isSelected: boolean;
  hasStickyHeader?: boolean;
  onAddItemToGroup: (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => void;
  onRemoveItemFromGroup: (item: ComparisonItem, group: ComparisonItemGroup) => void;
  onModifyGroup: (item: ComparisonItem) => void;
  showGroups?: boolean;
  editGroups?: boolean;
  locale?: Locale;
};

class ExpandedPortfolio extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loadingItems: true,
      holdingItems: [],
    };

    this.loadHoldingItems();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { holdings: newHoldings } = this.props.item;
    const { holdings: prevHoldings } = prevProps.item;

    const colorChanged = this.props.item.color !== prevProps.item.color;

    // If number of holdings has changed
    // or number is same but different ids - then refresh the holdings data
    if (
      !this.state.loadingItems &&
      (newHoldings.length !== prevHoldings.length ||
        newHoldings.some(h1 => !prevHoldings.some(h2 => h1.id === h2.id)) ||
        colorChanged ||
        this.props.comparison.monthEndDataOption !== prevProps.comparison.monthEndDataOption)
    ) {
      this.loadHoldingItems();
    }

    // If symbols have changed - then refresh the holdings data without clearing the precious dataset
    if (!this.state.loadingItems && newHoldings.some(h1 => !prevHoldings.some(h2 => h1.symbol === h2.symbol))) {
      this.loadHoldingItems(false);
    }

    if (this.state.holdingItems.length !== prevState.holdingItems.length) {
      dom.fireResizeEvent();
    }
  }

  loadHoldingItems = (clearCurrentHoldings = true) => {
    const { item, user } = this.props;

    if (clearCurrentHoldings) {
      this.setState({ holdingItems: [], loadingItems: true });
    }

    this.props.getItemHoldingsData(item.holdings).then(({ alts, benchmarks, funds, stocks }) => {
      const dataDict = [...alts, ...funds, ...stocks].reduce(
        (acc, d) => ({
          ...acc,
          [d._id]: d,
        }),
        {} as Dictionary<Holding>,
      );

      const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
      const holdingItems = holdingsToComparisonItems(item.holdings, dataDict, appRegion, benchmarks)
        .map(h => ({
          ...h,
          color: item.color,
        }))
        .filter(
          (holdingItem, i, arr) =>
            // Filter duplicates
            arr.map(value => value._id).indexOf(holdingItem._id) === i,
        );

      this.setState({
        holdingItems,
        loadingItems: false,
      });
    });
  };

  render() {
    const { holdingItems, loadingItems } = this.state;

    const {
      item,
      user,
      readOnly,
      onCellClick,
      selectedCategories,
      comparison,
      markupMode,
      sharingInfo,
      disableStickyHeader,
      onSavePortfolio,
      editedPortfolio,
      hasStickyHeader,
      scrollbarContainerRef,
      showGroups,
      editGroups,
      locale,
      ...rest
    } = this.props;

    return (
      <div className="expanded-portfolio">
        <div className="main-item">
          <ComparisonCardContent
            allowExpandPortfolio
            user={user}
            item={item}
            onCellClick={onCellClick}
            selectedCategories={selectedCategories}
            comparison={comparison}
            markupMode={markupMode}
            sharingInfo={sharingInfo}
            disableStickyHeader={disableStickyHeader}
            onSavePortfolio={onSavePortfolio}
            editedPortfolio={editedPortfolio}
            scrollbarContainerRef={scrollbarContainerRef}
            hasStickyHeader={hasStickyHeader}
            showGroups={showGroups}
            editGroups={editGroups}
            locale={locale}
            {...rest}
          />
        </div>

        {loadingItems && (
          <div className="expanded-sub-item portfolio-loading">
            <LoadingSpinner />
          </div>
        )}

        {!loadingItems &&
          holdingItems.map(holdingItem => (
            <div className="expanded-sub-item" key={holdingItem._id}>
              <ComparisonCardContent
                showMenu={false}
                user={user}
                item={holdingItem}
                onCellClick={onCellClick}
                selectedCategories={selectedCategories}
                comparison={comparison}
                markupMode={false}
                isExpandSubItem
                disableStickyHeader={disableStickyHeader}
                onSavePortfolio={onSavePortfolio}
                scrollbarContainerRef={scrollbarContainerRef}
                hasStickyHeader={hasStickyHeader}
                locale={locale}
                {...rest}
              />
            </div>
          ))}
      </div>
    );
  }
}

export default ExpandedPortfolio;
